import {FormError, FormValues} from '@wix/form-viewer'
import {FormViewerHandle} from '@wix/form-viewer/widget'
import {
  getBuyerFormId,
  getFormMessages,
  getRsvpFormId,
  isTicketed,
  isWaitlistOpen,
  useDidUpdate,
} from '@wix/wix-events-commons-statics'
import {detailsPageDataHooks as DH} from '@wix/wix-events-data-hooks'
import {TFunction} from '@wix/yoshi-flow-editor'
import React, {useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useAppSelector} from '../../../hooks/runtime'
import {getBuyerDetails} from '../../../selectors/checkout'
import {getDefaultFormValues, isMember} from '../../../selectors/current-member-details'
import {getEvent} from '../../../selectors/event'
import {getPlacedOrder} from '../../../selectors/placed-order'
import {hasPolicies as hasPoliciesSelector} from '../../../selectors/policies'
import {getRsvpDetails} from '../../../selectors/rsvp'
import {FormSubmitButton} from '../../form-submit-button'
import {WixForm, buildOverrides} from '../../wix-form'
import {getInitialValues} from '../utils'
import s from './buyer-details-form.scss'
import {BuyerDetailsFormProps} from './interfaces'

export const BuyerDetailsForm = ({onSubmit}: BuyerDetailsFormProps) => {
  const {t} = useTranslation()

  const event = useAppSelector(getEvent)
  const ticketed = isTicketed(event)
  const member = useAppSelector(isMember)
  const buyerDetails = useAppSelector(getBuyerDetails)
  const rsvpDetails = useAppSelector(getRsvpDetails)
  const defaultFormValues = useAppSelector(getDefaultFormValues)
  const placedOrder = useAppSelector(getPlacedOrder)
  const hasPolicies = useAppSelector(hasPoliciesSelector)
  const waitlistOpen = isWaitlistOpen(event)
  const messages = getFormMessages(event)

  const submitButtonText = getSubmitButtonText(messages, ticketed, hasPolicies, waitlistOpen, t)

  const formId = ticketed ? getBuyerFormId(event) : getRsvpFormId(event)
  const initialValues = getInitialValues({
    ticketed,
    buyerDetails,
    rsvpDetails,
    defaultFormValues,
    order: placedOrder,
  })
  const overrides = buildOverrides({
    initialValues,
    member,
  })

  const [formValues, setFormValues] = useState<FormValues>(initialValues)
  const [formErrors, setFormErrors] = useState<FormError[]>([])
  const formHandle = useRef<FormViewerHandle>(null)

  const submitForm = async () => {
    const valid = await formHandle.current?.validate()

    if (valid) {
      onSubmit(formValues)
    }
  }

  useDidUpdate(() => {
    if (member) {
      setFormValues(prev => ({...prev, ...initialValues}))
      setFormErrors([])
    }
  }, [member])

  return (
    <div className={s.container}>
      <WixForm
        formId={formId}
        values={formValues}
        errors={formErrors}
        overrides={overrides}
        onRegisterFormHandle={ref => (formHandle.current = ref)}
        onChange={setFormValues}
        onValidate={setFormErrors}
      />
      <FormSubmitButton dataHook={DH.FORM_BUTTON} text={submitButtonText} onClick={submitForm} />
    </div>
  )
}

const getSubmitButtonText = (
  messages: wix.events.form.FormMessages,
  ticketed: boolean,
  hasPolicies: boolean,
  waitlistOpen: boolean,
  t: TFunction,
) => {
  if (ticketed) {
    return messages.checkout.submitActionLabel || messages.rsvp.submitActionLabel
  }

  if (hasPolicies) {
    return t('mobile.checkout.next')
  }

  if (waitlistOpen) {
    return t('detailsPage.registrationForm.waitlist.submitButtonText')
  }

  return messages.rsvp.submitActionLabel
}
